﻿import React, { useEffect, Fragment, useState } from "react";
import moment from "moment";
import { parseDate } from "../../../../../Resources/Scripts/_Shared/parseDate";

import { useFacilityProducts } from "../hooks/useFacilityProducts";

import { TwoColumnWrapper } from "../../../../../Resources/Scripts/_Shared/elements/ColumnWrapper";
import { DateSelectionWithLabel } from "../../../../../Resources/Scripts/_Shared/elements/DateSelectionWithLabel";
import { DropdownlistWithLabel } from "../../../../../Resources/Scripts/_Shared/elements/DropdownlistWithLabel";
import { RadioButtonListWithLabel } from "../../../../../Resources/Scripts/_Shared/elements/RadioButtonListWithLabel";
import { LockedPmcPopup } from "../../../../../Resources/Scripts/_Shared/elements/Popup";
import LockedPmcForm from "../../../FacilityPage/Scripts/LockedPmcForm";

export const DateAndProductSelection = ({
    scrollEnabled,
    setScrollEnabled,
    bookingFlowContext,
    useRadioButtonList,
    formLang,
    pending,
    setFromUrl,
    date,
    setDate,
    productSelection,
    setProductSelection,
    dateDisabled,
    isAfterPriceChange,
    startDate,
    availableFrom,
}) => {
    const urlParams = new URLSearchParams(window.location.search);
    const [products, pendingProducts, hasPrivatePmcs] = useFacilityProducts(
        bookingFlowContext,
        productSelection,
        formLang.language,
        date,
    );
    const emptyGuid = "00000000-0000-0000-0000-000000000000";
    const language = bookingFlowContext.localization.language;
    const country = bookingFlowContext.country;
    const selectedLocale =
        country === "no" ? "nb" : country === "se" ? "sv" : country;
    const selectedLanguage =
        language === "no" ? "nb" : language === "se" ? "sv" : language;
    const hasPromo =
        Array.isArray(productSelection.item?.Promotions) &&
        productSelection.item.Promotions.length > 0;
    const flexDays =
        productSelection.item?.OnlineBookingFlexibleStartDate ?? null;
    const dropdownProducts = products
        ? products.map((zone) => zone.Item2).flat()
        : null;
    const imageAltText =
        selectedLanguage == "sv"
            ? "kampanj ikon"
            : selectedLanguage == "fi"
              ? "kampanjakuvake"
              : selectedLanguage == "nb"
                ? "kampanjeikon"
                : "campaign icon";

    const verifiedLockedCode =
        urlParams.get("verifiedLockedCode") ??
        urlParams.get("verifiedLockedCode");
    const verifiedPid = urlParams.get("pid") ?? urlParams.get("pId");

    const isProductUnlockedByCode = (product) => {
        if (!verifiedLockedCode || !verifiedPid) return false;
        return product.ZoneAndProduct === verifiedPid;
    };

    const [lockedPopupVisible, setLockedPopupVisible] = useState(false);
    const [selectedLockedProduct, setSelectedLockedProduct] = useState(null);

    const lockedPmcModalTitle =
        selectedLanguage == "sv"
            ? "Låst prenumeration!"
            : selectedLanguage == "fi"
              ? "Lukittu tilaus!"
              : selectedLanguage == "nb"
                ? "Låst abonnement!"
                : "Locked subscription!";
    const lockedPmcModalDescription =
        selectedLanguage == "sv"
            ? "Ange din kod för att låsa upp denna prenumeration."
            : selectedLanguage == "fi"
              ? "Syötä koodisi avataksesi tämän tilauksen."
              : selectedLanguage == "nb"
                ? "Skriv inn koden din for å låse opp dette abonnementet."
                : "Enter your code to unlock this subscription.";

    const handleLockedProductSelected = (selectedProduct) => {
        if (
            selectedProduct.Visibility === "LOCKED" &&
            verifiedLockedCode == null
        ) {
            setSelectedLockedProduct(selectedProduct);
            setLockedPopupVisible(true);
        }
    };

    useEffect(() => {
        if (!products) return;

        if (products.length > 0) {
            const urlPid = urlParams.get("pId") ?? urlParams.get("pid");
            if (
                (productSelection.item.Id === emptyGuid ||
                    productSelection.item.ZoneAndProduct === emptyGuid) &&
                urlPid
            ) {
                const selectedProduct = products
                    .map((item) =>
                        item.Item2.find((i) => i.ZoneAndProduct === urlPid),
                    )
                    .filter((product) => product !== undefined)[0];
                if (selectedProduct) {
                    setProductSelection({
                        ...productSelection,
                        item: selectedProduct,
                    });
                    setFromUrl(true);
                    return;
                }
            }

            /*  If entering the booking flow without a selected PMC,
             *  there is no pId in the URL.
             *  A valid pId must contain: <PlaceUid>-<pmcId>.
             *  Since pmcId is not known yet, set pId as <PlaceUid>-
             */
            if (!urlPid) {
                const firstProduct = products
                    .map((g) => g.Item2)
                    .flat()
                    .find(
                        (p) =>
                            p.ZoneAndProduct && p.ZoneAndProduct !== emptyGuid,
                    );
                if (firstProduct?.PlaceUid) {
                    const currentUrl = new URL(window.location.href);
                    const newPid = firstProduct.PlaceUid + "-";
                    currentUrl.searchParams.set("pid", newPid);
                    window.location.href = currentUrl.toString();
                }
            }
        } else {
            setProductSelection({
                ...productSelection,
                item: {
                    ...productSelection.item,
                    Text: formLang.errorMessages.noAvailableProducts,
                    Id: "00000000-0000-0000-0000-000000000000",
                },
                noproducts: true,
            });
        }
        setFromUrl(false);
    }, [products]);

    const createPromotText = (promotion) => {
        let promoTextFirstLine;
        let promoTextSecondLine;
        if (
            (promotion.DiscountPercent === 100 ||
                promotion.DiscountPercent === 50) &&
            promotion.StartAfter
        ) {
            const text =
                promotion.Duration > 1
                    ? formLang.promomonthfree.multipleduration
                    : formLang.promomonthfree.singleduration;
            promoTextFirstLine = text
                .replace("{0}", promotion.StartAfter + promotion.Duration)
                .replace("{1}", promotion.Duration);
            promoTextFirstLine =
                promoTextFirstLine +
                ` ${promotion.DiscountPercent === 100 ? formLang.promomonthfree.forfree : formLang.promomonthfree.halfprice}`;
            promoTextSecondLine = promotion.DiscountValidity?.ValidFrom
                ? `${formLang.promovalid}${promotion.DiscountValidity?.ValidTo ? "" : " " + formLang.promofrom.split(" ")[0]} ${parseDate(promotion.DiscountValidity.ValidFrom, selectedLocale, "L")}${promotion.DiscountValidity?.ValidTo ? "–" + parseDate(promotion.DiscountValidity.ValidTo, selectedLocale, "L") : ""}`
                : "";
        } else {
            promoTextFirstLine = `${formLang.promodiscount} ${Math.round(promotion.DiscountPercent || promotion.DiscountValue)}${promotion.DiscountValue ? " " + productSelection.item.Currency : "\u202F%"}`;
            promoTextSecondLine = promotion.DiscountValidity?.ValidFrom
                ? `${formLang.promovalid}${promotion.DiscountValidity?.ValidTo ? "" : " " + formLang.promofrom.split(" ")[0]} ${parseDate(promotion.DiscountValidity.ValidFrom, selectedLocale, "L")}${promotion.DiscountValidity?.ValidTo ? "–" + parseDate(promotion.DiscountValidity.ValidTo, selectedLocale, "L") : ""}`
                : promotion.Duration > 0
                  ? `${formLang.promovalid}${promotion.Duration ? (promotion.Duration > 1 ? " " + formLang.promodurations.replace("{0}", promotion.Duration) : " " + formLang.promoduration.replace("{0}", promotion.Duration)) : ""} ${formLang.promofrom} ${promotion.StartAfter + 1}`
                  : "";
        }
        return [promoTextFirstLine, promoTextSecondLine];
    };

    return (
        <>
            <TwoColumnWrapper>
                <DateSelectionWithLabel
                    flexDays={flexDays}
                    error={formLang.errorMessages.invalidDate}
                    text={formLang.startDate}
                    startDateExplanation={formLang.startDateExplanation}
                    startDateInfo={formLang.startDateInfo}
                    value={date}
                    startSellingDate={moment(
                        parseDate(productSelection.item?.StartSellingDate),
                    )
                        .add(6, "hours")
                        .locale(selectedLocale)
                        .format("L")}
                    setValue={setDate}
                    locale={selectedLocale}
                    language={selectedLanguage}
                    placeholder={formLang.selectdate}
                    showAllDates={false}
                    disabled={
                        (!products && pendingProducts) ||
                        pending ||
                        dateDisabled
                    }
                    country={bookingFlowContext.country}
                    startDate={startDate}
                    endDate={
                        bookingFlowContext.episerver.LatestSalesorderStartDay
                    }
                />
                {useRadioButtonList ? (
                    products ? (
                        products.map((zone, i) => {
                            return (
                                <Fragment key={i}>
                                    <h3 className="single-step__section__productlist__zone-heading">
                                        {zone.Item1.Name}
                                    </h3>
                                    <RadioButtonListWithLabel
                                        scrollEnabled={scrollEnabled}
                                        setScrollEnabled={setScrollEnabled}
                                        index={i}
                                        productList={true}
                                        isAfterPriceChange={isAfterPriceChange}
                                        selectedLanguage={selectedLanguage}
                                        formLang={formLang}
                                        modifierClass="single-step__section__productlist"
                                        selectedDate={date}
                                        text={formLang.product}
                                        available={formLang.available}
                                        locale={selectedLocale}
                                        options={zone.Item2 ?? []}
                                        value={productSelection}
                                        setValue={setProductSelection}
                                        availabilityUpdate={
                                            products && pendingProducts
                                        }
                                        loading={!products && pendingProducts}
                                        errorMessages={formLang.errorMessages}
                                        selectedDay={date.dayOfWeek}
                                        lockedCode={verifiedLockedCode}
                                        isLockedPmc={
                                            productSelection.item
                                                ?.Visibility === "LOCKED" &&
                                            !isProductUnlockedByCode(
                                                productSelection.item,
                                            )
                                        }
                                        onLockedProductSelected={
                                            handleLockedProductSelected
                                        }
                                    >
                                        {productSelection.item.Id !==
                                            "00000000-0000-0000-0000-000000000000" &&
                                            productSelection.item
                                                .Description && (
                                                <p>
                                                    {
                                                        productSelection.item
                                                            .Description
                                                    }
                                                </p>
                                            )}
                                        {productSelection.item.Id !==
                                            "00000000-0000-0000-0000-000000000000" &&
                                            productSelection.item.Notes && (
                                                <p>
                                                    {
                                                        productSelection.item
                                                            .Notes
                                                    }
                                                </p>
                                            )}
                                        {productSelection.item.Id !==
                                            "00000000-0000-0000-0000-000000000000" &&
                                            hasPromo &&
                                            productSelection.item.Promotions.map(
                                                (promotion, i) => {
                                                    const promoIsValid =
                                                        hasPromo &&
                                                        !!(
                                                            date.date &&
                                                            (!promotion
                                                                .OrderLineValidFromValidity
                                                                .ValidTo ||
                                                                moment(
                                                                    parseDate(
                                                                        date.date,
                                                                    ),
                                                                ).isSameOrBefore(
                                                                    parseDate(
                                                                        promotion
                                                                            .OrderLineValidFromValidity
                                                                            .ValidTo,
                                                                    ),
                                                                )) &&
                                                            moment(
                                                                parseDate(
                                                                    date.date,
                                                                ),
                                                            ).isSameOrAfter(
                                                                parseDate(
                                                                    promotion
                                                                        .OrderLineValidFromValidity
                                                                        .ValidFrom,
                                                                ),
                                                            )
                                                        );
                                                    const promoText =
                                                        createPromotText(
                                                            promotion,
                                                        );
                                                    return (
                                                        (!date.date ||
                                                            promoIsValid) && (
                                                            <div
                                                                className="promo-with-text"
                                                                key={
                                                                    "" +
                                                                    (promotion.DiscountPercent ||
                                                                        promotion.DiscountValue) +
                                                                    (promotion.Duration ||
                                                                        1) +
                                                                    i
                                                                }
                                                            >
                                                                <img
                                                                    src={`/publicResources/icons/promo/${selectedLanguage}/Promotion2_${selectedLanguage}.svg`}
                                                                    alt={
                                                                        imageAltText
                                                                    }
                                                                />
                                                                <div className="discount">
                                                                    <span>
                                                                        {
                                                                            promoText[0]
                                                                        }
                                                                    </span>
                                                                    {promoText[1] && (
                                                                        <span>
                                                                            {
                                                                                promoText[1]
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    );
                                                },
                                            )}
                                    </RadioButtonListWithLabel>
                                </Fragment>
                            );
                        })
                    ) : (
                        <RadioButtonListWithLabel
                            productList={true}
                            isAfterPriceChange={isAfterPriceChange}
                            selectedLanguage={selectedLanguage}
                            formLang={formLang}
                            modifierClass="single-step__section__productlist"
                            selectedDate={date}
                            text={formLang.product}
                            available={formLang.available}
                            locale={selectedLocale}
                            options={[]}
                            value={productSelection}
                            setValue={setProductSelection}
                            loading={!products && pendingProducts}
                            errorMessages={formLang.errorMessages}
                            selectedDay={date.dayOfWeek}
                            lockedCode={
                                isProductUnlockedByCode(productSelection.item)
                                    ? verifiedLockedCode
                                    : null
                            }
                            isLockedPmc={
                                productSelection.item?.Visibility ===
                                    "LOCKED" && verifiedLockedCode == null
                            }
                            onLockedProductSelected={
                                handleLockedProductSelected
                            }
                        />
                    )
                ) : (
                    <DropdownlistWithLabel
                        isAfterPriceChange={isAfterPriceChange}
                        selectedDate={date}
                        text={formLang.product}
                        available={formLang.available}
                        locale={selectedLocale}
                        options={dropdownProducts ?? []}
                        optionIdentifier="ZoneAndProduct"
                        value={productSelection}
                        setValue={setProductSelection}
                        disabled={pending || pendingProducts}
                        loading={pendingProducts}
                        errorMessages={formLang.errorMessages}
                        selectedDay={date.dayOfWeek}
                    >
                        {productSelection.item.Id !==
                            "00000000-0000-0000-0000-000000000000" &&
                            productSelection.item.Description && (
                                <p>{productSelection.item.Description}</p>
                            )}
                        {productSelection.item.Id !==
                            "00000000-0000-0000-0000-000000000000" &&
                            productSelection.item.Notes && (
                                <p>{productSelection.item.Notes}</p>
                            )}
                        {productSelection.item.Id !==
                            "00000000-0000-0000-0000-000000000000" &&
                            hasPromo &&
                            productSelection.item.Promotions.map(
                                (promotion, i) => {
                                    const promoIsValid =
                                        hasPromo &&
                                        !!(
                                            date.date &&
                                            (!promotion
                                                .OrderLineValidFromValidity
                                                .ValidTo ||
                                                moment(
                                                    parseDate(date.date),
                                                ).isSameOrBefore(
                                                    parseDate(
                                                        promotion
                                                            .OrderLineValidFromValidity
                                                            .ValidTo,
                                                    ),
                                                )) &&
                                            moment(
                                                parseDate(date.date),
                                            ).isSameOrAfter(
                                                parseDate(
                                                    promotion
                                                        .OrderLineValidFromValidity
                                                        .ValidFrom,
                                                ),
                                            )
                                        );
                                    const promoText =
                                        createPromotText(promotion);
                                    return (
                                        (!date.date || promoIsValid) && (
                                            <div
                                                className="promo-with-text"
                                                key={
                                                    "" +
                                                    (promotion.DiscountPercent ||
                                                        promotion.DiscountValue) +
                                                    (promotion.Duration || 1) +
                                                    i
                                                }
                                            >
                                                <img
                                                    src={`/publicResources/icons/promo/${selectedLanguage}/Promotion2_${selectedLanguage}.svg`}
                                                    alt={imageAltText}
                                                />
                                                <div className="discount">
                                                    <span>{promoText[0]}</span>
                                                    {promoText[1] && (
                                                        <span>
                                                            {promoText[1]}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    );
                                },
                            )}
                    </DropdownlistWithLabel>
                )}
            </TwoColumnWrapper>

            {hasPrivatePmcs && !bookingFlowContext.episerver.loggedIn && (
                <div>
                    <p>{formLang.privatepmcmore}</p>
                </div>
            )}
            {hasPrivatePmcs && bookingFlowContext.episerver.loggedIn && (
                <div>
                    <p>{formLang.privatepmcmoreloggedin}</p>
                </div>
            )}

            <LockedPmcPopup
                title={lockedPmcModalTitle}
                description={lockedPmcModalDescription}
                visible={lockedPopupVisible}
                onClose={() => setLockedPopupVisible(false)}
            >
                <LockedPmcForm
                    lang={selectedLanguage}
                    onSuccess={() => {
                        setLockedPopupVisible(false);
                    }}
                    pmcId={selectedLockedProduct?.Id}
                    onSubmit={() => {
                        setLockedPopupVisible(false);

                        const currentUrl = new URL(window.location.href);

                        //add verifiedLockedCode parameter
                        if (
                            !currentUrl.searchParams.has("verifiedLockedCode")
                        ) {
                            currentUrl.searchParams.append(
                                "verifiedLockedCode",
                                "checkedAndValid",
                            );
                        }

                        //update pid parameter
                        if (selectedLockedProduct?.ZoneAndProduct) {
                            currentUrl.searchParams.set(
                                "pid",
                                selectedLockedProduct.ZoneAndProduct,
                            );
                        }

                        window.location.href = currentUrl.toString();
                    }}
                />
            </LockedPmcPopup>
        </>
    );
};
